import { DndContext } from '@dnd-kit/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RescheduleButton } from 'shared/components/connected/reschedule-button';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { TaskCardTaskSection } from 'shared/components/ui/task-card-task-section';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useActiveTaskId } from 'shared/contexts/task-detail';
import { useDroppableDatesControls } from 'shared/hooks/use-droppable-dates-controls';
import { useHandleDateSectionDrop } from 'shared/hooks/use-handle-date-section-drop';
import { useSectionCollisionDetection } from 'shared/hooks/use-section-collision-detection';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { useUser } from 'shared/hooks/use-user';
import {
  DateSectionOptions,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { DueDisplayOptions } from 'shared/types/task-base';
import { parseIdDateKey } from 'shared/utils/format-id-date-key';
import { getTimeFormat } from 'shared/utils/get-time-format';

import { useUpcomingTaskSections } from '../../hooks/use-upcoming-task-sections';
import { sectionTypeToHeaderMap } from '../../types/date-section-options';
import { RestOfMonthHeader } from '../rest-of-month-header';
import { SectionHeaderOverdue } from '../section-header-overdue';

const displayDue = [DueDisplayOptions.Past, DueDisplayOptions.Future];
const displayDueDay: DueDisplayOptions[] = [];

export const TasksOverview: React.FC = () => {
  const { t } = useTranslation();
  const openPremiumDialog = useOpenPremiumDialog();
  const user = useUser();
  const timeFormat = getTimeFormat(user);

  const {
    taskCardTasks,
    sections: initialSections,
    taskSectionExclusionMap,
  } = useUpcomingTaskSections({
    timeFormat,
  });
  const onDragEnd = useHandleDateSectionDrop(initialSections);
  const {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    activeId,
    sections,
  } = useDroppableDatesControls({
    initialSections,
    onDragEnd,
    taskSectionExclusionMap,
  });
  const sensors = useSortableSensors();
  const collisionStrategy = useSectionCollisionDetection(activeId, sections);

  const selectedTask = useActiveTaskId();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const { activeItem, activeDisplayDate } = useMemo(() => {
    if (!activeId || !taskCardTasks.length) {
      return {};
    }

    const { date: activeDisplayDate } = parseIdDateKey(activeId);

    return {
      activeItem: taskCardTasks.find(({ id }) => id === activeId),
      activeDisplayDate,
    };
  }, [activeId, taskCardTasks]);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={collisionStrategy}
    >
      {sections.map(
        ({ id, items, deadline, startDate, endDate, type, externalEvents }) => {
          const headerToDisplay = sectionTypeToHeaderMap[type];
          const customHeader =
            type === DateSectionOptions.RestOfMonth ? (
              <RestOfMonthHeader startDate={startDate} endDate={endDate} />
            ) : undefined;

          if (id === overdueSectionID) {
            return items.length ? (
              <TaskCardTaskSection
                key={id}
                date={deadline}
                tasks={items}
                timeFormat={timeFormat}
                displayDue={displayDue}
                selectedTask={selectedTask}
                goals={goals}
                lifeAreas={lifeAreas}
                headerToDisplay={headerToDisplay}
                customHeader={
                  <SectionHeaderOverdue
                    title={t('general.sections.overdue.title')}
                    titleCount={items.length}
                    actions={[
                      <RescheduleButton
                        tasks={items.map(({ raw }) => raw.task)}
                      />,
                    ]}
                  />
                }
                droppableId={id}
                onPremium={openPremiumDialog}
                showRepeatingAsDoneOnDateEntry
                canCollapse
                canReorder
              />
            ) : null;
          }

          return (
            <TaskCardTaskSection
              key={id}
              date={deadline}
              tasks={items}
              displayDue={
                type === DateSectionOptions.Day ? displayDueDay : displayDue
              }
              displayDateRange={
                type !== DateSectionOptions.Day
                  ? { startDate, endDate }
                  : undefined
              }
              timeFormat={timeFormat}
              selectedTask={selectedTask}
              goals={goals}
              lifeAreas={lifeAreas}
              headerToDisplay={headerToDisplay}
              customHeader={customHeader}
              droppableId={id}
              canCollapse={id === overdueSectionID}
              canCreateTask={id !== overdueSectionID}
              onPremium={openPremiumDialog}
              externalEvents={externalEvents}
              showRepeatingAsDoneOnDateEntry
              canReorder
            />
          );
        },
      )}

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper>
            <TaskCard
              task={activeItem.raw.task}
              displayDue={displayDue}
              displayDate={activeDisplayDate}
            />
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
